import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Grid, Typography} from '@mui/material'
import {makeStyles, syskronPalette} from '@oc/styles'
import {SnackHandlerInstance} from '@oc/shell-core'
import {OcTextFieldLineType, OcTextField} from '../BasicOcComponents/OcTextFields'
import {OcFormRadio, OcFormCheckbox, OcFormSwitch} from '../BasicOcComponents/OcFormControl'
import OcAutocompleteSelect, {OCSelectOptionType} from '../BasicOcComponents/OcAutocompleteSelect'
import {messages} from '../translations'

/**
 * Converts object (recursively) to object in which type of each primitive property is replaced with `string | undefined`
 */
export type CustomValidationResult<T> = {
    [K in keyof T]: T[K] extends readonly [...infer Items]
        ? {
              [I in keyof Items]: Items[I] extends Record<string, any>
                  ? CustomValidationResult<Items[I]>
                  : string | undefined
          }
        : T[K] extends Array<infer Item>
        ? Item extends Record<string, any>
            ? CustomValidationResult<Item>[]
            : (string | undefined)[]
        : T[K] extends Record<string, any>
        ? CustomValidationResult<T[K]>
        : string | undefined
}

/**
 * @returns True if `validationResult` has any error message (recursively checked), else otherwise
 */
export function hasValidationError(validationResult: CustomValidationResult<unknown>): boolean {
    if (Array.isArray(validationResult)) {
        return validationResult.some(hasValidationError)
    }

    if (typeof validationResult === 'object') {
        return Object.values(validationResult).some(x => hasValidationError(x as CustomValidationResult<unknown>))
    }

    return !!validationResult as never
}

export interface OcGenericDialogFieldEditorBaseProps<FieldType> {
    getValue: () => FieldType
    changedValue?: FieldType
    isChanged: boolean
    disabled: boolean
    onChange: (value?: FieldType) => void
    errorMessage?: string
    /** Proposed type: CustomValidationResult<FieldType>, but not enforced */
    customValidationResult?: any
    helperText?: string
    fieldName: string
    label?: string
    mandatory?: boolean
    defaultValue?: FieldType
    createMode?: boolean
    placeholder?: string
    lineType?: OcTextFieldLineType
    maxRows?: number
    minRows?: number
}

type OcGenericDialogStringEditorProps = Omit<OcGenericDialogFieldEditorBaseProps<string>, 'customValidationResult'>

type OcGenericDialogCheckboxEditorProps = Omit<
    OcGenericDialogFieldEditorBaseProps<boolean>,
    | 'errorMessage'
    | 'helperText'
    | 'mandatory'
    | 'placeholder'
    | 'lineType'
    | 'maxRows'
    | 'minRows'
    | 'customValidationResult'
>

type OcGenericDialogSwitchEditorProps = Omit<
    OcGenericDialogFieldEditorBaseProps<boolean>,
    | 'errorMessage'
    | 'helperText'
    | 'mandatory'
    | 'placeholder'
    | 'lineType'
    | 'maxRows'
    | 'minRows'
    | 'customValidationResult'
>

interface OcGenericDialogRadiogroupEditorProps
    extends Omit<
        OcGenericDialogFieldEditorBaseProps<string | number>,
        | 'helperText'
        | 'mandatory'
        | 'label'
        | 'placeholder'
        | 'lineType'
        | 'maxRows'
        | 'minRows'
        | 'customValidationResult'
    > {
    suggestions: OCSelectOptionType[]
}

interface OcGenericDialogNumberEditorProps
    extends Omit<
        OcGenericDialogFieldEditorBaseProps<number>,
        'lineType' | 'maxRows' | 'minRows' | 'customValidationResult'
    > {
    allowDecimal?: boolean
    decimalPlaces?: number
    minValue?: number
    maxValue?: number
    changeStep?: number
}

interface OcGenericDialogEnumEditorProps
    extends Omit<
        OcGenericDialogFieldEditorBaseProps<OCSelectOptionType | OCSelectOptionType[] | undefined>,
        'lineType' | 'maxRows' | 'minRows' | 'customValidationResult'
    > {
    suggestions: OCSelectOptionType[]
    noOptionsMessage?: string
    multiSelect?: boolean
    defaultValue?: OCSelectOptionType
    stickyValues?: string[]
}

interface OcGenericDialogDateTimeEditorProps
    extends Omit<
        OcGenericDialogFieldEditorBaseProps<string | number>,
        'lineType' | 'maxRows' | 'minRows' | 'customValidationResult'
    > {
    minDate?: string
    maxDate?: string
    dateOnly?: boolean
}

const useStyles = makeStyles({
    formRoot: {
        padding: '6px 0',
        height: 'min-content'
    },
    radioErrorTypo: {
        paddingLeft: '12px'
    },
    formRootSwitch: {
        padding: '13px 0'
    }
})

function _OcGenericDialogStringEditor(props: OcGenericDialogStringEditorProps) {
    const {
        disabled,
        changedValue,
        isChanged,
        getValue,
        onChange,
        errorMessage,
        fieldName,
        helperText,
        label,
        mandatory,
        createMode,
        defaultValue,
        placeholder,
        lineType,
        maxRows,
        minRows
    } = props

    const value = isChanged ? changedValue : createMode ? defaultValue : getValue()

    const multiLineProps = React.useMemo(() => {
        switch (lineType) {
            case OcTextFieldLineType.Area:
                return {
                    lineType: lineType,
                    maxRows: maxRows,
                    minRows: minRows || 1
                }
            case OcTextFieldLineType.Multi:
                return {
                    lineType: lineType,
                    maxRows: maxRows,
                    minRows: minRows || 1
                }
            case OcTextFieldLineType.Single:
                return {
                    lineType: lineType,
                    maxRows: undefined,
                    minRows: undefined
                }
            default:
                return {}
        }
    }, [lineType, maxRows, minRows])

    return (
        <OcTextField
            value={typeof value === 'undefined' ? '' : value}
            label={label || fieldName}
            onChange={e => {
                onChange(e.target.value === '' ? undefined : e.target.value)
            }}
            disabled={disabled}
            helperText={errorMessage || helperText || '\u00A0'}
            error={!!errorMessage}
            mandatory={mandatory}
            fullWidth
            placeholder={placeholder}
            {...multiLineProps}
        />
    )
}

export const OcGenericDialogStringEditor = observer(_OcGenericDialogStringEditor)

const formatNumber = (value?: string, allowDecimal?: boolean, decimalPlaces?: number) => {
    let formattedValue: number
    if (typeof value === 'undefined' || value === '') {
        return undefined
    }

    if (allowDecimal) {
        formattedValue = parseFloat(value)
        if (decimalPlaces !== undefined) {
            formattedValue = parseFloat(formattedValue.toFixed(decimalPlaces))
        }
    } else {
        formattedValue = parseInt(value)
    }
    if (isNaN(formattedValue)) {
        return 0
    }
    return formattedValue
}

function _OcGenericDialogNumberEditor(props: OcGenericDialogNumberEditorProps) {
    const {
        disabled,
        changedValue,
        isChanged,
        getValue,
        onChange,
        errorMessage,
        allowDecimal,
        decimalPlaces,
        minValue,
        maxValue,
        fieldName,
        changeStep,
        helperText,
        label,
        mandatory,
        createMode,
        defaultValue,
        placeholder
    } = props

    const value = isChanged
        ? formatNumber(changedValue?.toString(), allowDecimal, decimalPlaces)
        : createMode
        ? formatNumber(defaultValue?.toString() || '', allowDecimal, decimalPlaces)
        : formatNumber(getValue()?.toString(), allowDecimal, decimalPlaces)

    const inputSteps = React.useMemo(() => {
        let steps: number
        if (changeStep) {
            return changeStep
        }
        if (allowDecimal) {
            if (decimalPlaces !== undefined) {
                steps = 1 / Math.pow(10, decimalPlaces)
            } else {
                steps = 0.1
            }
        } else {
            steps = 1
        }
        return steps
    }, [allowDecimal, decimalPlaces, changeStep])

    const onChangeCallback = React.useCallback(
        (value: string) => {
            const formattedValue = formatNumber(value, allowDecimal, decimalPlaces)
            if (typeof formattedValue !== 'undefined') {
                if (
                    (minValue ?? 0) > formattedValue ||
                    (typeof maxValue !== 'undefined' && maxValue < formattedValue)
                ) {
                    SnackHandlerInstance.error(
                        messages.getText(
                            'genericDialog',
                            'numberOutOfRangeMessage',
                            minValue ?? 0,
                            typeof maxValue !== 'undefined' ? maxValue : 'x'
                        )
                    )
                    return
                }
            }

            onChange(formattedValue)
        },
        [allowDecimal, decimalPlaces, onChange, minValue, maxValue]
    )

    return (
        <OcTextField
            value={typeof value === 'undefined' ? '' : value}
            label={label || fieldName}
            onChange={e => {
                onChangeCallback(e.target.value)
            }}
            disabled={disabled}
            type="number"
            inputProps={{step: inputSteps, min: minValue ?? 0, max: maxValue}}
            helperText={errorMessage || helperText || '\u00A0'}
            error={!!errorMessage}
            mandatory={mandatory}
            fullWidth
            placeholder={placeholder}
        />
    )
}

export const OcGenericDialogNumberEditor = observer(_OcGenericDialogNumberEditor)

function _OcGenericDialogCheckboxEditor(props: OcGenericDialogCheckboxEditorProps) {
    const {disabled, changedValue, isChanged, getValue, onChange, fieldName, label, createMode, defaultValue} = props
    const {classes} = useStyles()
    const value = isChanged ? changedValue : createMode ? defaultValue : getValue()

    return (
        <OcFormCheckbox
            checked={value || false}
            label={label || fieldName}
            onChange={(_, checked) => {
                onChange(checked)
            }}
            disabled={disabled}
            className={classes.formRoot}
        />
    )
}

export const OcGenericDialogCheckboxEditor = observer(_OcGenericDialogCheckboxEditor)

function _OcGenericDialogSwitchEditor(props: OcGenericDialogSwitchEditorProps) {
    const {disabled, changedValue, isChanged, getValue, onChange, fieldName, label, createMode, defaultValue} = props
    const {classes} = useStyles()
    const value = isChanged ? changedValue : createMode ? defaultValue : getValue()

    return (
        <OcFormSwitch
            checked={value || false}
            label={label || fieldName}
            onChange={(_, checked) => {
                onChange(checked)
            }}
            disabled={disabled}
            className={classes.formRootSwitch}
        />
    )
}

export const OcGenericDialogSwitchEditor = observer(_OcGenericDialogSwitchEditor)

function enumCombineValues(
    newValue: OCSelectOptionType | OCSelectOptionType[],
    baseValue?: OCSelectOptionType | OCSelectOptionType[]
): OCSelectOptionType | OCSelectOptionType[] {
    const result: OCSelectOptionType[] = []
    if (baseValue) {
        if (Array.isArray(baseValue)) {
            result.push(...baseValue)
        } else {
            result.push(baseValue)
        }
    }
    if (Array.isArray(newValue)) {
        result.push(...newValue)
    } else {
        result.push(newValue)
    }
    return result
}

function _OcGenericDialogEnumEditor(props: OcGenericDialogEnumEditorProps) {
    const {
        getValue,
        changedValue,
        isChanged,
        disabled,
        onChange,
        helperText,
        fieldName,
        label,
        suggestions,
        mandatory,
        noOptionsMessage,
        multiSelect,
        createMode,
        defaultValue,
        placeholder,
        errorMessage,
        stickyValues
    } = props

    const selectedValue = isChanged ? changedValue : createMode ? defaultValue : getValue()

    const onChangeCallback = React.useCallback(
        (selected: OCSelectOptionType | OCSelectOptionType[]) => {
            let value: OCSelectOptionType | OCSelectOptionType[] | undefined
            // array input - when removing items
            if (Array.isArray(selected)) {
                const sanitizedSelected = selected.map(item => {
                    return {label: item.label, value: item.value}
                })
                if (sanitizedSelected.length === 0) {
                    value = undefined
                } else if (multiSelect) {
                    value = sanitizedSelected
                } else {
                    value = sanitizedSelected[0]
                }
            } else if (selected) {
                const sanitizedSelected = {label: selected.label, value: selected.value}
                // multi select - needed to combine with existing value
                if (multiSelect) {
                    if (isChanged) {
                        const newValue = enumCombineValues(sanitizedSelected, changedValue)
                        value = newValue ? newValue : undefined
                    } else {
                        const newValue = enumCombineValues(sanitizedSelected, getValue())
                        value = newValue ? newValue : undefined
                    }
                }
                // single select - only overwrite value
                else {
                    value = sanitizedSelected
                }
            }

            if (stickyValues?.length) {
                const currentSelected = Array.isArray(selectedValue) ? selectedValue : []
                const stickySelected = currentSelected.filter(s => stickyValues.includes(s.value.toString()))

                if (Array.isArray(value)) {
                    const nonStickySelected = value.filter(item => !stickyValues.includes(item.value.toString()))
                    value = [...stickySelected, ...nonStickySelected]
                } else if (!value) {
                    // Edge case: if user removed everything (value becomes undefined),
                    // still restore the locked values
                    value = stickySelected.length > 0 ? stickySelected : undefined
                }
            }

            onChange(value)
        },
        [onChange, multiSelect, changedValue, getValue, isChanged, stickyValues, selectedValue]
    )

    return (
        <OcAutocompleteSelect
            placeholder={placeholder}
            value={typeof selectedValue === 'undefined' ? [] : selectedValue}
            suggestions={suggestions}
            label={label || fieldName}
            onChange={onChangeCallback}
            rootId={`oc-generic-dialog-field-${fieldName}`}
            helper={helperText}
            isClearable={!mandatory}
            mandatory={mandatory}
            noOptionsMessage={noOptionsMessage || messages.getText('genericDialog', 'noOptionsMessage')}
            menuPopUp={true}
            disabled={disabled}
            multi={multiSelect}
            errorMessage={errorMessage}
        />
    )
}

export const OcGenericDialogEnumEditor = observer(_OcGenericDialogEnumEditor)

function _OcGenericDialogRadiogroupEditor(props: OcGenericDialogRadiogroupEditorProps) {
    const {
        disabled,
        changedValue,
        isChanged,
        getValue,
        onChange,
        fieldName,
        suggestions,
        createMode,
        defaultValue,
        errorMessage
    } = props

    const {classes} = useStyles()

    const selectedValue = isChanged
        ? suggestions.find(item => item.value === changedValue)
        : createMode
        ? suggestions.find(item => item.value === defaultValue)
        : suggestions.find(item => item.value === getValue())

    const onChangeCallback = React.useCallback(
        (value: string | number) => {
            onChange(value)
        },
        [onChange]
    )

    return (
        <Grid container item xs={12}>
            {suggestions.map(item => {
                return (
                    <Grid item container xl={2} lg={3} md={6} sm={12} key={item.value} style={{padding: '0 12px'}}>
                        <OcFormRadio
                            className={classes.formRoot}
                            label={item.label}
                            value={item.value}
                            checked={item.value === selectedValue?.value}
                            data-checked={item.value === selectedValue?.value}
                            onChange={() => onChangeCallback(item.value)}
                            disabled={disabled}
                            radioGroup={fieldName}
                            controlStyle={errorMessage ? {color: syskronPalette.common.red} : undefined}
                        />
                    </Grid>
                )
            })}
            {errorMessage ? (
                <Grid container item xs={12}>
                    <Typography className={classes.radioErrorTypo} color="error">
                        {errorMessage}
                    </Typography>
                </Grid>
            ) : null}
        </Grid>
    )
}

export const OcGenericDialogRadiogroupEditor = observer(_OcGenericDialogRadiogroupEditor)

function timestampToString(timestamp: number, dateOnly: boolean): string {
    const date = new Date(timestamp)
    return dateOnly ? date.toISOString().split('T')[0] : date.toISOString()
}

function stringToTimestamp(dateString: string, dateOnly: boolean): number {
    const date = new Date(dateString)
    return dateOnly ? date.setHours(0, 0, 0, 0) : date.getTime()
}

function _OcGenericDialogDateTimeEditor(props: OcGenericDialogDateTimeEditorProps) {
    const {
        disabled,
        changedValue,
        isChanged,
        getValue,
        onChange,
        fieldName,
        label,
        helperText,
        createMode,
        defaultValue,
        mandatory,
        minDate,
        maxDate,
        dateOnly,
        errorMessage
    } = props

    const inputType = dateOnly ? 'date' : 'datetime-local'
    const initialValue = isChanged ? changedValue : createMode ? defaultValue : getValue()
    let value

    if (typeof initialValue === 'number') {
        value = timestampToString(initialValue, !!dateOnly)
    } else {
        value = initialValue
    }

    const handleOnChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value

            if (typeof initialValue === 'number') {
                const newTimestamp = stringToTimestamp(newValue, !!dateOnly)
                onChange(newTimestamp)
            } else {
                onChange(newValue)
            }
        },
        [onChange, dateOnly, initialValue]
    )

    const minDateString = typeof minDate === 'number' ? timestampToString(minDate, !!dateOnly) : minDate
    const maxDateString = typeof maxDate === 'number' ? timestampToString(maxDate, !!dateOnly) : maxDate

    return (
        <OcTextField
            type={inputType}
            label={label || fieldName}
            value={value}
            onChange={handleOnChange}
            disabled={disabled}
            helperText={errorMessage || helperText || '\u00A0'}
            error={!!errorMessage}
            mandatory={mandatory}
            fullWidth
            InputLabelProps={{
                shrink: true
            }}
            InputProps={{
                inputProps: {
                    min: minDateString,
                    max: maxDateString
                }
            }}
        />
    )
}

export const OcGenericDialogDateTimeEditor = observer(_OcGenericDialogDateTimeEditor)
